// QR-share for groups. The QR encodes a `norush:group:<base64-json>` URL
// containing the group id, name, and the list of (member_id, username)
// pairs. The joiner's app looks up each member's devices fresh from the
// relay on scan, so we don't bake stale pubkeys into the QR.
//
// Privacy posture: the QR contains member usernames + user ids. Both are
// world-readable on the relay already. Nothing secret travels in the QR.

import type { Friend, FriendDevice, Group } from '../types';

type GroupSharePayload = {
  v: 2; // bumped when we dropped per-user pubkeys
  id: string;
  name: string;
  members: Array<{ u: string; i: string }>;
};

export type ParsedGroupShare = {
  id: string;
  name: string;
  /** Members with empty devices arrays — caller is expected to backfill. */
  members: Friend[];
};

const PREFIX = 'norush:group:';

function safeBtoa(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}

function safeAtob(s: string): string {
  return decodeURIComponent(escape(atob(s)));
}

export function encodeGroupShareUrl(group: Group): string {
  const payload: GroupSharePayload = {
    v: 2,
    id: group.id,
    name: group.name,
    members: group.members.map((m) => ({
      u: m.username,
      i: m.id,
    })),
  };
  return PREFIX + safeBtoa(JSON.stringify(payload));
}

export function parseGroupShareUrl(url: string): ParsedGroupShare | null {
  if (!url.startsWith(PREFIX)) return null;
  try {
    const json = safeAtob(url.slice(PREFIX.length));
    const payload = JSON.parse(json) as GroupSharePayload;
    if (payload.v !== 2) return null;
    if (!payload.id || !payload.name || !Array.isArray(payload.members)) return null;
    return {
      id: payload.id,
      name: payload.name,
      members: payload.members.map((m) => ({
        id: m.i,
        username: m.u,
        devices: [] as FriendDevice[], // backfill on the joiner's side
      })),
    };
  } catch {
    return null;
  }
}
